import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-gray-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Par Seeker
        </Link>
        
        <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last Updated: June 15, 2026</p>
        
        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              Par Seeker ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, and safeguard your information when you use our golf score 
              tracking application.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect the following types of information:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Account Information:</strong> Name, email address, and password when you create an account</li>
              <li><strong className="text-white">Phone Number:</strong> Mobile phone number for account verification and notifications (optional)</li>
              <li><strong className="text-white">Golf Data:</strong> Scores, courses played, handicap information, and game history</li>
              <li><strong className="text-white">Social Data:</strong> Golf buddies, group memberships, and messages</li>
              <li><strong className="text-white">Usage Data:</strong> How you interact with our Service</li>
            </ul>
          </section>

          <section id="phone-privacy">
            <h2 className="text-xl font-semibold text-white mb-3">3. Phone Number and SMS Privacy</h2>
            <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4 mb-4">
              <p className="text-green-300 font-semibold mb-2">Important SMS Disclosures:</p>
              <ul className="space-y-2">
                <li><strong className="text-white">Non-Sharing:</strong> We will NOT share, sell, or rent your phone number to any third parties for marketing purposes.</li>
                <li><strong className="text-white">Message Frequency:</strong> Message frequency varies based on your activity. You may receive verification codes, game invitations, score notifications, and settlement reminders.</li>
                <li><strong className="text-white">Message and Data Rates:</strong> Standard message and data rates may apply depending on your mobile carrier plan.</li>
              </ul>
            </div>
            <p className="mb-3">
              Your phone number is used solely for:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Sending verification codes to secure your account</li>
              <li>Notifying you about game invitations from other players</li>
              <li>Sending score results and game updates</li>
              <li>Settlement reminders between players</li>
              <li>Important account security alerts</li>
            </ul>
          </section>

          <section id="sms-consent">
            <h2 className="text-xl font-semibold text-white mb-3">4. SMS Consent and Opt-In Process</h2>
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mb-4">
              <p className="text-blue-300 font-semibold mb-2">How You Opt-In to SMS Messages:</p>
              <p className="mb-3">
                End users opt-in to receive SMS messages through our web application at parseeker.com. 
                When you create an account or access your settings, you will be presented with a phone 
                number input form. The opt-in process includes:
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-2">
                <li>A phone number input field with country selector</li>
                <li>An <strong className="text-white">unchecked checkbox</strong> that you must actively select to consent</li>
                <li>Clear disclosure text stating: "Yes, I agree to receive automated text messages from Par Seeker including account verification codes, game invitations, score notifications, and settlement reminders. Message frequency varies. Message and data rates may apply."</li>
                <li>Links to this Privacy Policy and our Terms of Service</li>
              </ol>
            </div>
            
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 mb-4">
              <p className="text-amber-300 font-semibold mb-2">Opt-In Keywords:</p>
              <p className="mb-2">You may also opt-in by texting any of the following keywords to our number:</p>
              <p className="font-mono text-green-400 text-lg">YES, VERIFY, CONFIRM, START, SUBSCRIBE</p>
              <p className="mt-3 text-sm">
                <strong className="text-white">Opt-In Confirmation Message:</strong> "You have opted-in to receive verification codes and notifications from Par Seeker. Reply STOP to opt out. Msg&Data rates may apply. Reply HELP for help."
              </p>
            </div>

            <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-4">
              <p className="text-red-300 font-semibold mb-2">Opt-Out Instructions:</p>
              <p className="mb-2">You can opt out of SMS messages at any time by:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Replying <span className="font-mono text-green-400">STOP</span> to any message</li>
                <li>Other opt-out keywords: <span className="font-mono text-green-400">OPTOUT, CANCEL, END, QUIT, UNSUBSCRIBE, REVOKE, STOPAL</span></li>
              </ul>
              <p className="mt-3 text-sm">
                <strong className="text-white">Opt-Out Confirmation:</strong> "You have successfully been unsubscribed. You will not receive any more messages from this number. Reply START to resubscribe."
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-600/50 rounded-lg p-4">
              <p className="text-gray-300 font-semibold mb-2">Help Instructions:</p>
              <p className="mb-2">For assistance with SMS messages:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Reply <span className="font-mono text-green-400">HELP</span> or <span className="font-mono text-green-400">INFO</span> to any message</li>
                <li>Email us at <a href="mailto:support@parseeker.com" className="text-green-500 hover:underline">support@parseeker.com</a></li>
              </ul>
              <p className="mt-3 text-sm">
                <strong className="text-white">Help Response:</strong> "Par Seeker SMS Help: Reply STOP to unsubscribe. Msg&Data rates may apply. For support, email support@parseeker.com or visit parseeker.com/privacy"
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. How We Use Your Information</h2>
            <p className="mb-3">We use your information to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Provide and maintain the Service</li>
              <li>Calculate and track your golf handicap</li>
              <li>Connect you with other golfers</li>
              <li>Send notifications about games and settlements</li>
              <li>Improve our Service</li>
              <li>Communicate with you about updates and support</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Information Sharing</h2>
            <p className="mb-3">We may share your information only in the following circumstances:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong className="text-white">With Other Players:</strong> Your name, handicap, and scores may be visible to other players in shared games</li>
              <li><strong className="text-white">Service Providers:</strong> We use trusted third parties like Twilio for SMS delivery. These providers are contractually bound to protect your information.</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="mt-3 text-green-400 font-medium">
              We do NOT sell your personal information to advertisers or data brokers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information, including encryption 
              of data in transit and at rest. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Your Rights and Choices</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Correction:</strong> Update or correct your information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your account and data</li>
              <li><strong className="text-white">Opt-Out of SMS:</strong> Reply STOP to any text message to unsubscribe</li>
              <li><strong className="text-white">SMS Help:</strong> Reply HELP to any text message for assistance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active. If you delete your account, 
              we will delete your personal information within 30 days, except as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Children's Privacy</h2>
            <p>
              Our Service is not intended for users under 13 years of age. We do not knowingly collect 
              information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, contact us at:{' '}
              <a href="mailto:privacy@parseeker.com" className="text-green-500 hover:underline">
                privacy@parseeker.com
              </a>
            </p>
          </section>
        </div>
        
        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-gray-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Par Seeker. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
